import Paper from '@/components/Paper'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { ExcelExportButton } from '@/components/ui/data-table-customized'
import { FilterBar } from '@/components/ui/data-table-customized'
import { EMPLOYEE_EXCEL_COLUMNS } from '../../constants/employees'
import { useEmployeeList } from '../../hooks/use-employee-list'

interface EmployeeListProps {
    /** Force a status filter (e.g. [1] for working, [0] for resigned).
     *  Hides status from FilterBar and locks the value for this view. */
    forcedStatus?: number[]
    /** URL param prefix so each tab keeps its own page/filter state */
    urlPrefix?: string
    /** Initial sort column when URL has no sort_by (default 'employee_status') */
    defaultSortBy?: string
    /** Initial sort order */
    defaultSortOrder?: 'asc' | 'desc'
}

const EmployeeList = ({
    forcedStatus,
    urlPrefix,
    defaultSortBy,
    defaultSortOrder,
}: EmployeeListProps = {}) => {
    const {
        employees,
        pagination,
        stateComponent,
        control,
        setFilter,
        columns,
        actions,
        filterItems,
        params,
        queryParams,
        searchInputProps,
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        t,
    } = useEmployeeList({
        forcedStatus,
        urlPrefix,
        defaultSortBy,
        defaultSortOrder,
    })

    return (
        <Paper className="space-y-2">
            {/* Filters */}
            <div data-tour="employee-filter-bar">
                <FilterBar
                    control={control}
                    setFilter={setFilter}
                    items={filterItems}
                    hideSearch
                />
            </div>

            {/* Table */}
            {stateComponent || (
                <div data-tour="employee-data-table">
                    <DataTableCustomized
                        columns={columns}
                        rows={employees}
                        rowKey={(row) => row.employee_id}
                        resizable
                        size="small"
                        toolbar={{
                            search: {
                                ...searchInputProps,
                                placeholder: t('search_employee'),
                                tourId: 'employee-search',
                            },
                            settings: {
                                show: true,
                                storageKey: 'employee-list',
                                tourId: 'employee-settings',
                                actionComponents: [
                                    <ExcelExportButton
                                        key="export"
                                        apiEndpoint="employees"
                                        columns={EMPLOYEE_EXCEL_COLUMNS}
                                        queryParams={queryParams}
                                        feature="employee"
                                    />,
                                ],
                            },
                        }}
                        sorting={{
                            sortBy: params.sort_by,
                            sortOrder: params.sort_order,
                            onSortChange: handleSortChange,
                        }}
                        actions={actions}
                        stickyHeader={{
                            enabled: true,
                            offsetTopSelector: '#main-header',
                        }}
                        pagination={{
                            page: params.page,
                            pageSize: params.page_size,
                            total: pagination?.total || 0,
                            onPageChange: handlePageChange,
                            onPageSizeChange: handlePageSizeChange,
                        }}
                    />
                </div>
            )}
        </Paper>
    )
}

export default EmployeeList
